import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ChevronRight, PlayCircle, Loader2, CheckCircle, XCircle, Copy, Clock } from 'lucide-react';
import { supabase } from '@/lib/supabase';

interface Operation {
  id: string;
  key: string;
  direction: string;
  family: string;
  graphql_name: string;
  input_type: string | null;
  sample_query: string;
  sample_variables_json: any;
  description: string;
}

const ApiRunnerPage = () => {
  const [searchParams] = useSearchParams();
  const operationParam = searchParams.get('operation');

  const [operations, setOperations] = useState<Operation[]>([]);
  const [selectedOperation, setSelectedOperation] = useState<Operation | null>(null);
  const [variables, setVariables] = useState('{}');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [duration, setDuration] = useState<number>(0);

  useEffect(() => {
    loadOperations();
  }, []);

  useEffect(() => {
    if (operationParam && operations.length > 0) {
      const op = operations.find(o => o.id === operationParam);
      if (op) {
        selectOperation(op);
      }
    }
  }, [operationParam, operations]);

  const loadOperations = async () => {
    if (!supabase) return;

    setLoading(true);
    try {
      const { data: provider } = await supabase
        .from('integration_providers')
        .select('id')
        .eq('slug', 'successware')
        .single();

      if (!provider) return;

      const { data, error } = await supabase
        .from('integration_operations')
        .select('*')
        .eq('provider_id', provider.id)
        .order('key', { ascending: true });

      if (error) throw error;

      setOperations(data || []);
    } catch (error) {
      console.error('Error loading operations:', error);
    } finally {
      setLoading(false);
    }
  };

  const selectOperation = (op: Operation) => {
    setSelectedOperation(op);
    setQuery(op.sample_query);
    setVariables(JSON.stringify(op.sample_variables_json, null, 2));
    setResult(null);
    setError(null);
  };

  const executeOperation = async () => {
    if (!selectedOperation) return;

    setExecuting(true);
    setError(null);
    setResult(null);

    const startTime = Date.now();

    try {
      // Get access token
      if (!supabase) throw new Error('Supabase not initialized');

      const { data: provider } = await supabase
        .from('integration_providers')
        .select('id')
        .eq('slug', 'successware')
        .single();

      if (!provider) throw new Error('SuccessWare provider not found');

      const { data: creds } = await supabase
        .from('integration_credentials')
        .select('access_token, token_expires_at')
        .eq('provider_id', provider.id)
        .single();

      if (!creds?.access_token) {
        throw new Error('No access token found. Please authenticate first.');
      }

      // Check if token is expired
      if (creds.token_expires_at && new Date(creds.token_expires_at) < new Date()) {
        throw new Error('Access token expired. Please re-authenticate.');
      }

      // Parse variables
      let parsedVariables = {};
      try {
        parsedVariables = JSON.parse(variables);
      } catch (e) {
        throw new Error('Invalid JSON in variables');
      }

      // Execute GraphQL query
      const response = await fetch('https://publicapi.successwareg2.com/api/graphql', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${creds.access_token}`
        },
        body: JSON.stringify({
          query,
          variables: parsedVariables
        })
      });

      const endTime = Date.now();
      setDuration(endTime - startTime);

      const data = await response.json();

      if (data.errors) {
        setError(data.errors[0]?.message || 'GraphQL error occurred');
        setResult({ errors: data.errors });

        // Log error
        await supabase.from('integration_logs').insert({
          provider_id: provider.id,
          operation_id: selectedOperation.id,
          correlation_id: `runner_${Date.now()}`,
          request_json: { query, variables: parsedVariables },
          response_json: data,
          status_code: response.status,
          success: false,
          error_summary: data.errors[0]?.message,
          duration_ms: endTime - startTime
        });
      } else {
        setResult(data);

        // Log success
        await supabase.from('integration_logs').insert({
          provider_id: provider.id,
          operation_id: selectedOperation.id,
          correlation_id: `runner_${Date.now()}`,
          request_json: { query, variables: parsedVariables },
          response_json: data,
          status_code: response.status,
          success: true,
          duration_ms: endTime - startTime
        });
      }

    } catch (err: any) {
      const endTime = Date.now();
      setDuration(endTime - startTime);
      setError(err.message || 'Execution failed');
    } finally {
      setExecuting(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <div className="p-6">
      <div className="mb-8">
        <div className="flex items-center gap-2 text-slate-400 text-sm mb-4">
          <Link to="/integrations" className="hover:text-white">Integrations</Link>
          <ChevronRight className="w-4 h-4" />
          <Link to="/integrations/successware" className="hover:text-white">SuccessWare</Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-white">API Runner</span>
        </div>
        <h1 className="text-3xl font-bold text-white mb-2">API Runner</h1>
        <p className="text-slate-400">
          Test SuccessWare operations with live data and inspect responses
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Operation Selector */}
        <div className="lg:col-span-1">
          <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
            <h3 className="font-semibold text-white mb-4">Select Operation</h3>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
              </div>
            ) : (
              <div className="space-y-1 max-h-[600px] overflow-y-auto">
                {operations.map((op) => (
                  <button
                    key={op.id}
                    onClick={() => selectOperation(op)}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                      selectedOperation?.id === op.id
                        ? 'bg-blue-600 text-white'
                        : 'text-slate-300 hover:bg-slate-800'
                    }`}
                  >
                    <div className="font-medium text-sm">{op.key.replace(/_/g, ' ')}</div>
                    <div className="text-xs opacity-75">{op.graphql_name}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Editor & Results */}
        <div className="lg:col-span-2 space-y-6">
          {selectedOperation ? (
            <>
              {/* Operation Info */}
              <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
                <h3 className="font-semibold text-white mb-2">{selectedOperation.key.replace(/_/g, ' ')}</h3>
                <p className="text-slate-400 text-sm mb-3">{selectedOperation.description}</p>
                <div className="flex gap-4 text-sm">
                  <div>
                    <span className="text-slate-400">Type:</span>{' '}
                    <span className="text-white capitalize">{selectedOperation.direction}</span>
                  </div>
                  <div>
                    <span className="text-slate-400">Family:</span>{' '}
                    <span className="text-white capitalize">{selectedOperation.family}</span>
                  </div>
                  {selectedOperation.input_type && (
                    <div>
                      <span className="text-slate-400">Input:</span>{' '}
                      <code className="text-blue-400">{selectedOperation.input_type}</code>
                    </div>
                  )}
                </div>
              </div>

              {/* Query Editor */}
              <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="font-medium text-white">Query/Mutation</label>
                  <button
                    onClick={() => copyToClipboard(query)}
                    className="text-slate-400 hover:text-white text-sm flex items-center gap-1"
                  >
                    <Copy className="w-4 h-4" />
                    Copy
                  </button>
                </div>
                <textarea
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="w-full h-48 bg-slate-950 border border-slate-700 rounded-lg p-3 text-sm text-slate-300 font-mono"
                  spellCheck={false}
                />
              </div>

              {/* Variables Editor */}
              <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="font-medium text-white">Variables (JSON)</label>
                  <button
                    onClick={() => copyToClipboard(variables)}
                    className="text-slate-400 hover:text-white text-sm flex items-center gap-1"
                  >
                    <Copy className="w-4 h-4" />
                    Copy
                  </button>
                </div>
                <textarea
                  value={variables}
                  onChange={(e) => setVariables(e.target.value)}
                  className="w-full h-32 bg-slate-950 border border-slate-700 rounded-lg p-3 text-sm text-slate-300 font-mono"
                  spellCheck={false}
                />
              </div>

              {/* Execute Button */}
              <button
                onClick={executeOperation}
                disabled={executing}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-700 disabled:text-slate-400 text-white font-medium rounded-lg transition-colors"
              >
                {executing ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Executing...
                  </>
                ) : (
                  <>
                    <PlayCircle className="w-5 h-5" />
                    Execute Operation
                  </>
                )}
              </button>

              {/* Result */}
              {(result || error) && (
                <div className={`border rounded-lg p-4 ${
                  error
                    ? 'bg-red-500/10 border-red-500/30'
                    : 'bg-green-500/10 border-green-500/30'
                }`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      {error ? (
                        <>
                          <XCircle className="w-5 h-5 text-red-400" />
                          <span className="font-semibold text-red-300">Error</span>
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-5 h-5 text-green-400" />
                          <span className="font-semibold text-green-300">Success</span>
                        </>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-400">
                      <Clock className="w-4 h-4" />
                      {duration}ms
                    </div>
                  </div>
                  {error && (
                    <p className="text-red-300 mb-3">{error}</p>
                  )}
                  <pre className="bg-slate-950 border border-slate-800 rounded-lg p-4 text-sm text-slate-300 overflow-x-auto">
                    {JSON.stringify(result, null, 2)}
                  </pre>
                </div>
              )}
            </>
          ) : (
            <div className="bg-slate-900 border border-slate-800 rounded-lg p-12 text-center">
              <PlayCircle className="w-12 h-12 mx-auto mb-4 text-slate-600" />
              <p className="text-slate-400">Select an operation from the left to begin testing</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ApiRunnerPage;
